class CartTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const tabBtn = e.target.closest('[data-tab-toggle]');
      if (tabBtn) {
        this.toggleTab(tabBtn.dataset.tabToggle);
        return;
      }
      if (e.target.closest('[data-tab-cancel]')) {
        this.closeTabs();
        return;
      }
      if (this.classList.contains('cart-tabs--drawer') && e.target.classList.contains('cart-tabs__sheet')) {
        this.closeTabs();
      }
    });

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-shipping-country]')) this.syncProvinces(e.target);
    });

    this.addEventListener('submit', (e) => {
      if (e.target.matches('[data-note-form]')) {
        e.preventDefault();
        this.saveNote(e.target);
        return;
      }
      if (e.target.matches('[data-coupon-form]')) {
        e.preventDefault();
        this.saveCoupon(e.target);
        return;
      }
      if (e.target.matches('[data-shipping-form]')) {
        e.preventDefault();
        this.calculateShipping(e.target);
      }
    });
  }

  toggleTab(name) {
    if (this.activeTab === name) {
      this.closeTabs();
      return;
    }
    this.showTab(name);
  }

  showTab(name) {
    const alreadyOpen = Boolean(this.activeTab);
    this.activeTab = name;
    this.setAttribute('data-open', name);
    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== name;
    });
    this.querySelectorAll('[data-tab-toggle]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.tabToggle === name);
    });
    if (name === 'shipping') {
      const countrySelect = this.querySelector('[data-shipping-country]');
      if (countrySelect) this.initShippingCountry(countrySelect);
    }
    if (this.classList.contains('cart-tabs--drawer')) this.openDrawerSheet(alreadyOpen);
  }

  closeTabs() {
    if (this.classList.contains('cart-tabs--drawer') && this.activeTab) {
      this.closeDrawerSheet();
      return;
    }
    this.resetTabs();
  }

  openDrawerSheet(alreadyOpen) {
    const sheet = this.querySelector('.cart-tabs__sheet');
    const panel = this.querySelector('[data-tab-panel]:not([hidden])');
    if (!sheet || !panel) return;
    sheet.style.display = 'flex';
    sheet.style.backgroundColor = 'white';
    if (alreadyOpen) {
      panel.style.transform = 'translateY(0)';
      return;
    }
    panel.style.transform = 'translateY(100%)';
    setTimeout(() => {
      panel.style.transform = 'translateY(0)';
    }, 10);
  }

  closeDrawerSheet() {
    if (this._closing) return;
    this._closing = true;
    const sheet = this.querySelector('.cart-tabs__sheet');
    const panel = this.querySelector('[data-tab-panel]:not([hidden])');
    if (sheet) sheet.style.backgroundColor = 'rgba(0, 0, 0, 0)';
    if (panel) panel.style.transform = 'translateY(100%)';
    setTimeout(() => {
      if (sheet) {
        sheet.style.display = 'none';
        sheet.style.backgroundColor = '';
      }
      this.querySelectorAll('[data-tab-panel]').forEach((p) => {
        p.style.transform = '';
      });
      this._closing = false;
      this.resetTabs();
    }, 300);
  }

  resetTabs() {
    this.activeTab = null;
    this.removeAttribute('data-open');
    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      panel.hidden = true;
    });
    this.querySelectorAll('[data-tab-toggle]').forEach((btn) => {
      btn.classList.remove('is-active');
    });
  }

  initShippingCountry(countrySelect) {
    const def = countrySelect.dataset.default;
    if (def && !countrySelect.dataset.initialized) {
      const match = [...countrySelect.options].find(
        (opt) => opt.value === def || opt.textContent === def
      );
      if (match) countrySelect.value = match.value;
      countrySelect.dataset.initialized = 'true';
    }
    this.syncProvinces(countrySelect);
  }

  syncProvinces(countrySelect) {
    const wrap = this.querySelector('[data-shipping-province-wrap]');
    const select = this.querySelector('[data-shipping-province]');
    if (!wrap || !select) return;

    const option = countrySelect.selectedOptions[0];
    let provinces = [];
    try {
      provinces = JSON.parse(option?.getAttribute('data-provinces') || '[]');
    } catch (err) {
      provinces = [];
    }

    select.innerHTML = '';
    if (!provinces.length) {
      wrap.hidden = true;
      select.required = false;
      return;
    }

    wrap.hidden = false;
    select.required = true;
    provinces.forEach((entry) => {
      const name = Array.isArray(entry) ? entry[0] : entry;
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name;
      select.appendChild(opt);
    });
  }

  async saveNote(form) {
    const submit = form.querySelector('[type="submit"]');
    this.setFormError(form, '');
    if (submit) submit.disabled = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: form.note.value || '' }),
      });
      if (!res.ok) throw new Error('Could not save note');
      this.closeTabs();
    } catch (err) {
      this.setFormError(form, err.message);
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  async saveCoupon(form) {
    const submit = form.querySelector('[type="submit"]');
    const code = (form.discount.value || '').trim();
    this.setFormError(form, '');
    if (!code) {
      this.setFormError(form, 'Enter a discount code');
      return;
    }
    if (submit) submit.disabled = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      const cart = await res.json();
      if (!res.ok) {
        throw new Error(cart.description || cart.message || 'Could not apply discount');
      }
      const codes = cart.discount_codes || [];
      const applied = codes.some(
        (entry) =>
          entry.code &&
          entry.code.toLowerCase() === code.toLowerCase() &&
          entry.applicable !== false
      );
      const apps = cart.cart_level_discount_applications || [];
      const titled = apps.some(
        (app) => (app.title || '').toLowerCase() === code.toLowerCase()
      );
      if (!applied && !titled) {
        throw new Error('Discount code is invalid');
      }
      this.closeTabs();
      document.querySelector('cart-page')?.refresh?.();
      document.querySelector('cart-drawer')?.refresh?.();
    } catch (err) {
      this.setFormError(form, err.message);
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  async calculateShipping(form) {
    const zip = (form.querySelector('[data-shipping-zip]')?.value || '').trim();
    const country = form.querySelector('[data-shipping-country]')?.value || '';
    const provinceWrap = this.querySelector('[data-shipping-province-wrap]');
    const province = form.querySelector('[data-shipping-province]')?.value || '';
    const results = this.querySelector('[data-shipping-rates]');
    const submit = form.querySelector('[type="submit"]');

    this.setFormError(form, '');
    if (results) {
      results.hidden = true;
      results.innerHTML = '';
    }

    if (!zip) {
      this.setFormError(form, 'Enter a postcode *');
      return;
    }

    if (submit) submit.disabled = true;
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': country,
    });
    if (provinceWrap && !provinceWrap.hidden && province) {
      params.set('shipping_address[province]', province);
    }

    try {
      await fetch(`/cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });
      const rates = await this.pollShippingRates(params);
      if (!rates.length) {
        this.setFormError(form, 'No shipping rates available for this address');
        return;
      }
      if (results) {
        results.innerHTML = rates
          .map((rate) => {
            const name = rate.presentment_name || rate.name;
            const price = rate.price === '0.00' || rate.price === 0 ? 'Free' : rate.price;
            return `<li><span>${name}</span><span>${price}</span></li>`;
          })
          .join('');
        results.hidden = false;
      }
    } catch (err) {
      this.setFormError(form, err.message || 'Could not calculate shipping rates');
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  async pollShippingRates(params) {
    for (let i = 0; i < 12; i += 1) {
      const res = await fetch(`/cart/async_shipping_rates.json?${params}`);
      if (res.status === 202) {
        await this.wait(400);
        continue;
      }
      const data = await res.json();
      if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
      if (data === null) {
        await this.wait(400);
        continue;
      }
      if (data.error) throw new Error(Array.isArray(data.error) ? data.error[0] : data.error);
    }
    throw new Error('Shipping rates took too long. Try again, or continue to checkout.');
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  setFormError(form, message) {
    const el = form.querySelector('[data-tab-error]');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }
}

customElements.define('cart-tabs', CartTabs);
